import { useEffect, useRef, useState } from "react";
import { useApi } from "./useApi";
import { useChatPusher } from "./useChatPusher";
import { useChatNotifications } from "./useChatNotifications";

/**
 * Owns chat data + Pusher wiring for a single room-list + selected-room view.
 * Shared by the full chat page and (future) chat modal.
 */
export const useChatRoom = ({ roomId, onError } = {}) => {
  const { get, post } = useApi();
  const { get: searchGet } = useApi();
  const { onMessageSent } = useChatPusher();
  const { refreshUnseenCount } = useChatNotifications();

  const [contacts, setContacts] = useState([]);
  const [chatUserId, setChatUserId] = useState(null);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [messages, setMessages] = useState(null);
  const [isMessagesLoading, setIsMessagesLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const fetchMessages = (id) => {
    if (!id) return;
    setIsMessagesLoading(true);
    post(
      "/chat/v1/chatify/api/fetchMessages",
      { chat_room_id: id },
      {
        useChatToken: true,
        cancelable: true,
        onSuccess: (response) => {
          setMessages(response.data.messages);
          // fetchMessages always returns the room's contact info, unlike
          // getContacts which only lists rooms already in the chatify contact
          // list (e.g. a patient opened fresh from the health dashboard may
          // not be there yet). Use it as the source of truth for the room's
          // name, merging over any richer contact-list match for avatar/unseen.
          const contact = response.data.contact;
          if (contact) {
            setSelectedRoom((prev) => ({
              ...(prev?.id == contact.id ? prev : {}),
              id: contact.id,
              name: contact.name,
            }));
          }
        },
        onError,
        onFinally: () => setIsMessagesLoading(false),
      }
    );
  };

  const getContacts = () => {
    setIsLoading(true);
    get("/chat/v1/chatify/api/getContacts", {
      useChatToken: true,
      onSuccess: (response) => {
        setContacts(response.data.contacts);
        setChatUserId(response.data.chat_user_id);
      },
      onError,
      onFinally: () => setIsLoading(false),
    });
  };

  const searchContacts = (input) => {
    setIsLoading(true);
    searchGet(`/chat/v1/chatify/api/search?input=${encodeURIComponent(input)}`, {
      useChatToken: true,
      cancelable: true,
      onSuccess: (response) => setContacts(response.data.contacts),
      onError,
      onFinally: () => setIsLoading(false),
    });
  };

  const makeSeen = (id) => {
    post(
      "/chat/v1/chatify/api/makeSeen",
      { ch_room_id: id },
      {
        useChatToken: true,
        onSuccess: () => refreshUnseenCount(),
        onError,
      }
    );
  };

  const sendMessage = ({ message, file, onSent } = {}) => {
    if (!file && !message) return;
    const formData = new FormData();
    formData.append("file", file);
    formData.append("chat_room_id", selectedRoom?.id);
    formData.append("type", "user");
    formData.append("message", message);
    post("/chat/v1/chatify/api/sendMessage", formData, {
      useChatToken: true,
      headers: { "Content-Type": "multipart/form-data" },
      onSuccess: () => {
        fetchMessages(selectedRoom?.id);
        if (onSent) onSent();
      },
      onError,
    });
  };

  // Debounced search
  useEffect(() => {
    if (searchInput.length === 0) {
      getContacts();
      return;
    }
    const timeout = setTimeout(() => searchContacts(searchInput), 500);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  // Enrich the selected room with contact-list metadata (avatar/unseen) when
  // available. Not the source of truth for the room existing — a patient
  // opened fresh from the health dashboard may not be in the chatify contact
  // list yet, so this only merges in extra fields when there's a match.
  useEffect(() => {
    if (!roomId) return;
    const room = contacts.find((contact) => contact.id == roomId);
    if (room) setSelectedRoom((prev) => ({ ...prev, ...room }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId, contacts]);

  // Fetch messages only when the target room actually changes
  const seenRoomIdRef = useRef(null);
  useEffect(() => {
    setMessages(null);
    seenRoomIdRef.current = null;
    if (!roomId) {
      setSelectedRoom(null);
      return;
    }
    setSelectedRoom({ id: roomId });
    fetchMessages(roomId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId]);

  // Mark seen once per room open, not on every contact-list refresh
  useEffect(() => {
    if (!selectedRoom?.unseen) return;
    if (seenRoomIdRef.current === selectedRoom.id) return;
    seenRoomIdRef.current = selectedRoom.id;
    makeSeen(selectedRoom.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRoom]);

  // Live updates for the currently viewed room + contact list refresh
  useEffect(() => {
    return onMessageSent((data) => {
      if (!data?.message) return;
      if (roomId && roomId == data.message.ch_room_id) {
        fetchMessages(data.message.ch_room_id);
      }
      getContacts();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId]);

  return {
    contacts,
    chatUserId,
    selectedRoom,
    messages,
    isMessagesLoading,
    isLoading,
    searchInput,
    setSearchInput,
    sendMessage,
  };
};
